import React, { Component } from "react";
import * as echarts from "echarts";
import moment from "moment";
import format from "date-fns/format";

const chartFields = {
    rms: ["Bx", "By", "Bz"],
    phase: ["Bx", "By", "Bz"],
    vmon: ["VBatt", "VPV"],
    housekeeping: ["VBatt", "VPV", "Temperature"],
    vbatt: ["VBatt"],
    rssi: ["SignalQuality"],
    ghi: ["ghi_percent", "chance_of_precip"],
    volt: ["cg_supply_voltage"],
    temperature: ["ambientTempC", "bottomBoardTempC"],
    percent: ["relativeHumidity"],
    point: ["compressed_count"],
    dewBurn: ["operational_mode"],
};

const fieldColors = ["#ff6961", "#77dd77", "#779ecb", "#ffb347"];

export const SERIES_LABEL_BY_TYPE = {
    volt: "Volt(s)",
    temperature: "Degrees (°C)",
    percent: "%",
    point: "# of Points",
    rms: "RMS",
    phase: "Phase",
    vmon: "Vmon",
    housekeeping: "Housekeeping",
    vbatt: "Battery Voltage",
    rssi: "RSSI",
    ghi: "Percent(%)",
    dewBurn: "Dew Burn Status",
};

const seriesLabel = {
    cg_supply_voltage: "Supply Voltage",
    ambientTempC: "Ambient Temp.",
    bottomBoardTempC: "Bottom Board Temp.",
    relativeHumidity: "Relative Humidity",
    compressed_count: "Compressed Count",
    operational_mode: "0-Normal, 1-Burn",
};

export default class DeviceChart extends Component {
    constructor(props) {
        super(props);
        this.handleResize = this.handleResize.bind(this);
    }

    handleResize() {
        this.updateDimensions();
    }

    componentDidMount() {
        this.createChart();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.data !== this.props.data) {
            this.createChart();
        }
    }

    createChart() {
        const { data, type } = this.props;

        this.chart = echarts.init(document.getElementById("main_" + type));

        if (data?.length) {
            const fields = chartFields[type];
            let dataSeries;

            if (type == "dewBurn") {
                dataSeries = fields.map((f, idx) => {
                    return {
                        name: f,
                        type: "line",
                        step: "end",
                        color: fieldColors[idx],
                        animation: false,
                        symbol: "none",
                        symbolSize: 6,
                        connectNulls: true,
                    };
                });
            } else {
                dataSeries = fields.map((f, idx) => {
                    return {
                        name: f,
                        type: "line",
                        color: fieldColors[idx],
                        animation: false,
                        symbol: "none",
                        symbolSize: 6,
                        connectNulls: true,
                    };
                });
            }

            let dataGroups = {};

            data.forEach((datum) => {
                for (let key in datum) {
                    if (fields.includes(key)) {
                        const dataValue = datum[key];
                        const dttm = datum.dttm;
                        const dt = echarts.time.format("yyyy-MM-dd hh:mm:ss", dttm, true);

                        if (dataGroups[key] === undefined) dataGroups[key] = [];

                        dataGroups[key].push({
                            name: dttm,
                            value: [dt, dataValue],
                        });
                    }
                }
            });

            for (let key in dataGroups) {
                const series = dataSeries.find((series) => series.name === key);
                const dataValues = dataGroups[key];
                series["data"] = dataValues;
                series.name = seriesLabel[series.name] || series.name;
            }

            const options = {
                title: {
                    text: SERIES_LABEL_BY_TYPE[type],
                },
                legend: {},
                useUTC: true,
                tooltip: {
                    confine: false,
                    trigger: "axis",
                    axisPointer: {
                        animation: false,
                        type: "line",
                    },
                    position: function (pos, _, __, ___, size) {
                        const topPos = pos[1] - size.contentSize[1] / 2;
                        let leftPos = pos[0] + 50;

                        if (leftPos + 250 > size.viewSize[0]) {
                            leftPos = pos[0] - 275;
                        }

                        return { top: topPos, left: leftPos };
                    },
                    formatter: function (params) {
                        let content = "";
                        params.forEach((param, idx) => {
                            if (param.seriesName == "operational_mode") {
                                param.axisDim = "x";
                            }
                            if (idx === 0) {
                                const date = param.value[0];
                                const formattedDate = moment(date).format("MM/DD HH:mm");
                                content += `<div>${formattedDate} GMT+0</div> `;
                            }

                            content += `<div>${param.marker} ${param.seriesName} : ${param.value[1]}</div>`;
                        });

                        return content;
                    },
                },
                grid: {
                    left: "10%",
                    top: 40,
                    right: 10,
                    bottom: "20%",
                },
                xAxis: {
                    type: "time",
                    axisLabel: {
                        formatter: function (date) {
                            return format(date, "MM/dd HH:mm aaa");
                        },
                        showMaxLabel: false,
                    },
                    splitNumber: 3,
                },
                yAxis: {
                    type: "value",
                    min: "dataMin",
                    max: "dataMax",
                    scale: true,
                },
                dataZoom: [
                    {
                        type: "inside",
                    },
                    {
                        type: "inside",
                    },
                ],
                series: dataSeries,
            };

            this.chart.setOption(options);
        }

        window.addEventListener("resize", this.handleResize);
    }

    updateDimensions() {
        if (!this.chart) {
            return;
        }

        this.chart.resize();

        const width = this.chart.getWidth();

        this.chart.setOption({
            xAxis: {
                splitNumber: Math.max(1, Math.floor(width / 100) - 2),
            },
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
        if (this.chart) {
            this.chart.dispose();
        }
    }

    render() {
        return <div id={`main_${this.props.type}`} style={{ width: "80%", height: "250px", margin: "1rem 0" }}></div>;
    }
}
